// FireIncidentDrillsTable.tsx
import React from 'react';

export interface FireIncidentDrillsRow {
  srNo: number;
  systems: string;
  actualNo: number | null;
  targetNo: number | null;
  process: string | null;
  status: string | null;
  completionDateOrRemark: string | null;
}

interface FireIncidentDrillsTableProps {
  data?: FireIncidentDrillsRow[];
  onChange?: (index: number, updatedRow: FireIncidentDrillsRow) => void;
  isEditable?: boolean;
}

const statusOptions = ['YES', 'NO', 'N/A'];

// Sr.Nos linked to KPI Fire Safety Scorecard parameters (must match syncMISToKPI.ts)
const KPI_LINKED_SR_NOS: number[] = [60, 61, 62, 63, 64];

export default function FireIncidentDrillsTable({
  data = [],
  onChange,
  isEditable = true,
}: FireIncidentDrillsTableProps) {
  const handleFieldChange = (
    idx: number,
    field: keyof FireIncidentDrillsRow,
    value: string | number | null
  ) => {
    if (!onChange || !isEditable) return;

    const row = data[idx];
    let processedValue: string | number | null = value;

    // Handle numeric fields with validation
    if (field === 'actualNo' || field === 'targetNo') {
      if (typeof value === 'string') {
        if (value.toLowerCase() === 'na' || value.toLowerCase() === 'n/a' || value === '') {
          processedValue = null;
        } else {
          const numValue = parseFloat(value);
          processedValue = isNaN(numValue) ? null : numValue;
        }
      }

      // Validate actualNo doesn't exceed targetNo
      if (field === 'actualNo' && processedValue !== null && row.targetNo !== null) {
        if (typeof processedValue === 'number' && processedValue > row.targetNo) {
          alert(`Actual number (${processedValue}) cannot exceed Target number (${row.targetNo})`);
          return;
        }
      }
    }

    // Prevent editing of non-editable fields
    if (field === "srNo" || field === "systems" || field === "process") {
      return; // Prevent editing of srNo, systems, and process
    }

    onChange(idx, {
      ...row,
      [field]: processedValue,
    });
  };

  const formatValue = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '';
    return value.toString();
  };

  const getDisplayValue = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '';
    return value.toString();
  };

  return (
    <div className="w-full overflow-x-auto">
      <div className="bg-purple-600 text-white px-4 py-2 font-bold text-lg mb-0 rounded-t-lg">
        (III) FIRE INCIDENT / ACCIDENTS, DRILLS & TRAINING PROGRAMME
      </div>
      <table className="min-w-full border-collapse border border-gray-300 bg-white">
        <thead>
          <tr className="bg-gray-200 border-b-2 border-gray-400">
            <th className="border border-gray-300 px-3 py-2 text-center font-bold text-sm text-gray-800 w-20">
              Sr.No
            </th>
            <th className="border border-gray-300 px-4 py-2 text-left font-bold text-sm text-gray-800 min-w-[400px] max-w-[500px]">
              Systems
            </th>
            <th className="border border-gray-300 px-3 py-2 text-center font-bold text-sm text-gray-800 w-32">
              Actual no.
            </th>
            <th className="border border-gray-300 px-3 py-2 text-center font-bold text-sm text-gray-800 w-32">
              Target No.
            </th>
            {/* <th className="border border-gray-300 px-4 py-2 text-left font-bold text-sm text-gray-800 min-w-[300px]">
              Process
            </th> */}
            <th className="border border-gray-300 px-3 py-2 text-center font-bold text-sm text-gray-800 min-w-[120px] w-32">
              Status
            </th>
            <th className="border border-gray-300 px-4 py-2 text-left font-bold text-sm text-gray-800 min-w-[250px] max-w-[350px]">
              Completion date / Remark
            </th>
          </tr>
        </thead>
        <tbody>
          {data.map((row, idx) => {
            const isLinked = KPI_LINKED_SR_NOS.includes(row.srNo);
            return (
              <tr
                key={idx}
                className={`transition-colors ${isLinked
                  ? 'bg-teal-50 hover:bg-teal-100 border-l-4 border-l-teal-500'
                  : 'bg-white hover:bg-gray-50'
                  }`}
                title={isLinked ? 'Linked to KPI Fire Safety Scorecard' : undefined}
              >
                <td className={`border border-gray-300 px-3 py-2 text-center text-sm font-medium ${isLinked ? 'text-teal-800' : 'text-gray-700'
                  }`}>
                  {isLinked && <span className="inline-block w-2 h-2 rounded-full bg-teal-500 mr-1" />}
                  <span>{row.srNo || ''}</span>
                </td>
                <td className="border border-gray-300 px-4 py-2 text-sm text-gray-700">
                  <span className="whitespace-pre-wrap break-words">{row.systems || ''}</span>
                </td>
                <td className="border border-gray-300 px-3 py-2 text-center">
                  {isEditable ? (
                    <input
                      type="number"
                      min="0"
                      max={row.targetNo || undefined}
                      value={getDisplayValue(row.actualNo)}
                      onChange={(e) => handleFieldChange(idx, 'actualNo', e.target.value)}
                      placeholder="N/A"
                      className={`w-full px-2 py-1 text-center border border-gray-400 rounded bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${row.actualNo !== null && row.targetNo !== null && row.actualNo > row.targetNo
                        ? 'border-red-500 bg-red-50'
                        : ''
                        }`}
                    />
                  ) : (
                    <span className="text-sm text-gray-700 font-medium">
                      {row.actualNo !== null && row.actualNo !== undefined ? row.actualNo : 'N/A'}
                    </span>
                  )}
                </td>
                <td className="border border-gray-300 px-3 py-2 text-center">
                  {isEditable ? (
                    <input
                      type="number"
                      min="0"
                      value={getDisplayValue(row.targetNo)}
                      onChange={(e) => handleFieldChange(idx, 'targetNo', e.target.value)}
                      placeholder="N/A"
                      className="w-full px-2 py-1 text-center border border-gray-400 rounded bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  ) : (
                    <span className="text-sm text-gray-700 font-medium">
                      {row.targetNo !== null && row.targetNo !== undefined ? row.targetNo : 'N/A'}
                    </span>
                  )}
                </td>
                {/* <td className="border border-gray-300 px-4 py-2 text-sm text-gray-700">
                  {isEditable ? (
                    <textarea
                      value={row.process || ''}
                      onChange={(e) => handleFieldChange(idx, 'process', e.target.value || null)}
                      rows={2}
                      className="w-full px-2 py-1 border border-gray-400 rounded bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
                      placeholder="Enter process"
                    />
                  ) : (
                    <span className="whitespace-pre-wrap break-words">{row.process || 'N/A'}</span>
                  )}
                </td> */}
                <td className="border border-gray-300 px-3 py-2 text-center min-w-[120px]">
                  {isEditable ? (
                    <select
                      value={row.status || ''}
                      onChange={(e) => handleFieldChange(idx, 'status', e.target.value || null)}
                      className="w-full px-2 py-1 text-center border border-gray-400 rounded bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm min-w-[100px]"
                    >
                      <option value="">Select Status</option>
                      {statusOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className="text-sm text-gray-700 font-medium inline-block px-2 py-1 bg-gray-100 rounded">
                      {row.status || 'N/A'}
                    </span>
                  )}
                </td>
                <td className="border border-gray-300 px-4 py-2">
                  {(() => {
                    const statusLower = (row.status || '').toLowerCase();
                    const requiresRemarks = statusLower === 'no' || statusLower === 'pending';
                    const isMissing = requiresRemarks && !row.completionDateOrRemark?.trim();
                    return isEditable ? (
                      <div>
                        <textarea
                          value={row.completionDateOrRemark || ''}
                          onChange={(e) => handleFieldChange(idx, 'completionDateOrRemark', e.target.value || null)}
                          placeholder={requiresRemarks ? 'Remarks required *' : 'Enter completion date or remark'}
                          rows={2}
                          className={`w-full px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:border-transparent resize-y ${isMissing
                            ? 'border-red-400 bg-red-50 focus:ring-red-500 placeholder-red-400'
                            : 'border-gray-400 bg-white focus:ring-blue-500'
                            }`}
                        />
                        {isMissing && (
                          <p className="text-xs text-red-500 mt-1">Remarks required for "{row.status}" status</p>
                        )}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-700 whitespace-pre-wrap break-words">{row.completionDateOrRemark || 'N/A'}</span>
                    );
                  })()}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}



